/** PKCE (Proof Key for Code Exchange) 工具，用于 OAuth 2.0 SPA 授权流程 */

function toBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let str = ''
  for (const b of bytes) str += String.fromCharCode(b)
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 * SHA-256 纯 JS 降级实现
 * 仅在 http:// IP 访问（非安全上下文）时使用，crypto.subtle 不可用
 */
function sha256Fallback(msgBytes: Uint8Array): ArrayBuffer {
  // RFC 6234 / FIPS 180-4 SHA-256
  const K = new Uint32Array([
    0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
    0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
    0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
    0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
    0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
    0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
    0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
    0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
  ])
  let h = new Uint32Array([
    0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19,
  ])
  const bitLen = msgBytes.length * 8
  // 填充
  const padLen = ((msgBytes.length + 9 + 63) & ~63)
  const padded = new Uint8Array(padLen)
  padded.set(msgBytes)
  padded[msgBytes.length] = 0x80
  const dv = new DataView(padded.buffer)
  dv.setUint32(padLen - 4, bitLen >>> 0, false)
  dv.setUint32(padLen - 8, Math.floor(bitLen / 0x100000000), false)
  // 压缩
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n))
  for (let i = 0; i < padLen; i += 64) {
    const w = new Uint32Array(64)
    for (let j = 0; j < 16; j++) w[j] = dv.getUint32(i + j * 4, false)
    for (let j = 16; j < 64; j++) {
      const s0 = rotr(w[j-15], 7) ^ rotr(w[j-15], 18) ^ (w[j-15] >>> 3)
      const s1 = rotr(w[j-2], 17) ^ rotr(w[j-2], 19) ^ (w[j-2] >>> 10)
      w[j] = (w[j-16] + s0 + w[j-7] + s1) >>> 0
    }
    let [a,b,c,d,e,f,g,hh] = [...h]
    for (let j = 0; j < 64; j++) {
      const S1 = rotr(e,6) ^ rotr(e,11) ^ rotr(e,25)
      const ch = (e & f) ^ (~e & g)
      const t1 = (hh + S1 + ch + K[j] + w[j]) >>> 0
      const S0 = rotr(a,2) ^ rotr(a,13) ^ rotr(a,22)
      const maj = (a & b) ^ (a & c) ^ (b & c)
      const t2 = (S0 + maj) >>> 0
      hh=g; g=f; f=e; e=(d+t1)>>>0; d=c; c=b; b=a; a=(t1+t2)>>>0
    }
    h[0]=(h[0]+a)>>>0; h[1]=(h[1]+b)>>>0; h[2]=(h[2]+c)>>>0; h[3]=(h[3]+d)>>>0
    h[4]=(h[4]+e)>>>0; h[5]=(h[5]+f)>>>0; h[6]=(h[6]+g)>>>0; h[7]=(h[7]+hh)>>>0
  }
  const out = new Uint8Array(32)
  h.forEach((v, i) => new DataView(out.buffer).setUint32(i * 4, v, false))
  return out.buffer
}

/** 生成随机 code_verifier（43~128 位 URL 安全字符） */
export function generateCodeVerifier(): string {
  const arr = new Uint8Array(64)
  crypto.getRandomValues(arr)
  return toBase64Url(arr.buffer)
}

/** 由 code_verifier 计算 code_challenge (S256) */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier)
  // crypto.subtle 仅在安全上下文（https / localhost）下可用
  // 通过 http:// IP 访问时降级为纯 JS 实现
  const hash = crypto.subtle
    ? await crypto.subtle.digest('SHA-256', data)
    : sha256Fallback(data)
  return toBase64Url(hash)
}

/** 生成随机 state 防 CSRF */
export function generateState(): string {
  const arr = new Uint8Array(16)
  crypto.getRandomValues(arr)
  return toBase64Url(arr.buffer)
}
